'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import { UserAvatar } from '@/components/images/user-avatar';
import type { Referee } from '@/components/portfolio/referrals/types';
import { UserIdentity } from '@/components/profile/user-identity';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/app-tooltip';
import { Icon } from '@/components/ui/icon';
import { ArrowDataTransferHorizontalIcon, ChartBarLineIcon, DollarCircleIcon, GitBranchIcon } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { defaultTimeZone } from '@/i18n/locales';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { useUserProfilePrefetchIntent } from '@/lib/sdk/prefetch';
interface RefereeCardProps {
  referee: Referee;
}
export function RefereeCard(t0) {
  const $ = _c(68);
  const {
    referee
  } = t0;
  const t = useTranslations("social.referrals");
  const locale = useLocale();
  const {
    formatD6
  } = useFormattedCurrency();
  const intent = useUserProfilePrefetchIntent(referee.refereeUserId);
  let t1;
  if ($[0] !== locale || $[1] !== referee.linkedAt) {
    t1 = new Date(referee.linkedAt).toLocaleDateString(locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: defaultTimeZone
    });
    $[0] = locale;
    $[1] = referee.linkedAt;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const linkedDate = t1;
  const t2 = `/portfolio/${referee.refereeUserId}`;
  const t3 = referee.avatarPreferences?.rasterise ?? false;
  let t4;
  if ($[3] !== referee.avatarNsfw || $[4] !== referee.avatarUrl || $[5] !== referee.displayName || $[6] !== referee.refereeUserId || $[7] !== t3) {
    t4 = <UserAvatar userId={referee.refereeUserId} displayName={referee.displayName} avatarUrl={referee.avatarUrl} nsfw={referee.avatarNsfw} size="md" rasterise={t3} />;
    $[3] = referee.avatarNsfw;
    $[4] = referee.avatarUrl;
    $[5] = referee.displayName;
    $[6] = referee.refereeUserId;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== intent || $[10] !== t2 || $[11] !== t4) {
    t5 = <Link href={t2} {...intent}>{t4}</Link>;
    $[9] = intent;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = `/portfolio/${referee.refereeUserId}`;
  const t7 = referee.avatarNsfw === true;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [];
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== referee.avatarUrl || $[15] !== referee.displayName || $[16] !== referee.handle || $[17] !== referee.refereeUserId || $[18] !== t7) {
    t9 = <UserIdentity user={{
      userId: referee.refereeUserId,
      displayName: referee.displayName,
      handle: referee.handle,
      avatarUrl: referee.avatarUrl,
      avatarNsfw: t7,
      connectedProviders: t8
    }} size="sm" />;
    $[14] = referee.avatarUrl;
    $[15] = referee.displayName;
    $[16] = referee.handle;
    $[17] = referee.refereeUserId;
    $[18] = t7;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== intent || $[21] !== t6 || $[22] !== t9) {
    t10 = <Link href={t6} {...intent}>{t9}</Link>;
    $[20] = intent;
    $[21] = t6;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== referee.hasDownstream || $[25] !== t) {
    t11 = referee.hasDownstream && <TooltipProvider><Tooltip><TooltipTrigger asChild={true}><Icon icon={GitBranchIcon} className="size-3.5 text-foreground-secondary" aria-label={t("referee_has_referrals")} /></TooltipTrigger><TooltipContent>{t("referee_has_referrals_tooltip")}</TooltipContent></Tooltip></TooltipProvider>;
    $[24] = referee.hasDownstream;
    $[25] = t;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11) {
    t12 = <div className="flex items-center gap-1.5">{t10}{t11}</div>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Icon icon={ArrowDataTransferHorizontalIcon} className="size-3" />;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const t14 = String(referee.totalTrades);
  let t15;
  if ($[31] !== t14) {
    t15 = <span className="tabular-nums">{t14}</span>;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t) {
    t16 = t("referee_trades");
    $[33] = t;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t16) {
    t17 = <span>{t16}</span>;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t15 || $[38] !== t17) {
    t18 = <span className="flex items-center gap-1">{t13}{t15}{t17}</span>;
    $[37] = t15;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Icon icon={ChartBarLineIcon} className="size-3" />;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== formatD6 || $[42] !== referee.totalVolume) {
    t20 = formatD6(referee.totalVolume);
    $[41] = formatD6;
    $[42] = referee.totalVolume;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t20) {
    t21 = <span className="flex items-center gap-1">{t19}<span className="tabular-nums">{t20}</span></span>;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Icon icon={DollarCircleIcon} className="size-3" />;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== formatD6 || $[48] !== referee.earningsGenerated) {
    t23 = formatD6(referee.earningsGenerated);
    $[47] = formatD6;
    $[48] = referee.earningsGenerated;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t23) {
    t24 = <span className="flex items-center gap-1">{t22}<span className="tabular-nums">{t23}</span></span>;
    $[50] = t23;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== t18 || $[53] !== t21 || $[54] !== t24) {
    t25 = <div className="flex flex-wrap gap-x-4 gap-y-1 text-caption text-foreground-secondary">{t18}{t21}{t24}</div>;
    $[52] = t18;
    $[53] = t21;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t) {
    t26 = t("referee_linked");
    $[56] = t;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== linkedDate || $[59] !== t26) {
    t27 = <span className="text-caption text-foreground-secondary">{t26} {linkedDate}</span>;
    $[58] = linkedDate;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== t12 || $[62] !== t25 || $[63] !== t27) {
    t28 = <div className="flex flex-1 flex-col gap-1">{t12}{t25}{t27}</div>;
    $[61] = t12;
    $[62] = t25;
    $[63] = t27;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== t28 || $[66] !== t5) {
    t29 = <Panel><PanelContent className="flex items-start gap-3">{t5}{t28}</PanelContent></Panel>;
    $[65] = t28;
    $[66] = t5;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  return t29;
}